import { useState, type ChangeEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import {
  ArrowLeft,
  Target,
  User,
  Mail,
  Calendar,
  DollarSign,
  CheckCircle,
  XCircle,
  FileText,
  Loader2,
  AlertCircle,
  RefreshCw,
  Building2,
  Edit,
  Save,
  Package,
  Plus,
  Trash2,
} from 'lucide-react';
import { cn, formatDate, formatCurrency } from '@/lib/utils';
import { Modal } from '@/components/ui/Modal';
import {
  GET_OPPORTUNITY,
  UPDATE_OPPORTUNITY,
  MARK_OPPORTUNITY_WON,
  MARK_OPPORTUNITY_LOST,
  DELETE_OPPORTUNITY,
  ADD_OPPORTUNITY_PRODUCT,
  REMOVE_OPPORTUNITY_PRODUCT,
  type OpportunityQueryResult,
  type OpportunityStage,
  type OpportunityProduct,
} from '@/graphql/opportunities';
import { GET_PRODUCTS, type Product, type GetProductsData } from '@/graphql/products';

const STAGE_BADGES: Record<OpportunityStage, { label: string; className: string }> = {
  DISCOVERY: { label: 'Discovery', className: 'badge-primary' },
  PROPOSAL: { label: 'Proposal', className: 'badge-warning' },
  NEGOTIATION: { label: 'Negotiation', className: 'bg-violet/10 text-violet dark:bg-violet-light/20 dark:text-violet-light' },
  WON: { label: 'Won', className: 'badge-secondary' },
  LOST: { label: 'Lost', className: 'badge-neutral' },
};

const OPPORTUNITY_STAGES = [
  { value: 'DISCOVERY', label: 'Discovery', probability: 25 },
  { value: 'PROPOSAL', label: 'Proposal', probability: 50 },
  { value: 'NEGOTIATION', label: 'Negotiation', probability: 75 },
];

const LOSS_REASONS = [
  'Price too high',
  'Feature gaps',
  'Timing not right',
  'Went with incumbent',
  'Budget constraints',
  'Other',
];

export function OpportunityDetail() {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  
  // Modal states
  const [showWonModal, setShowWonModal] = useState(false);
  const [showLostModal, setShowLostModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Won modal state
  const [wonNotes, setWonNotes] = useState('');

  // Lost modal state
  const [lostToCompetitor, setLostToCompetitor] = useState('');
  const [lostReason, setLostReason] = useState('');

  // Edit form state
  const [editValue, setEditValue] = useState('');
  const [editStage, setEditStage] = useState('');
  const [editProbability, setEditProbability] = useState('');
  const [editExpectedClose, setEditExpectedClose] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editIsRecurring, setEditIsRecurring] = useState(false);

  // Add product state
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [newProductPrice, setNewProductPrice] = useState('');
  const [newProductQuantity, setNewProductQuantity] = useState('1');
  const [newProductCustomSku, setNewProductCustomSku] = useState('');

  // Fetch products for dropdown
  const { data: productsData } = useQuery<GetProductsData>(GET_PRODUCTS, {
    variables: { isActive: true, limit: 100 },
  });
  const productsList = productsData?.products?.items || [];

  // GraphQL query
  const { data, loading, error, refetch } = useQuery<OpportunityQueryResult>(
    GET_OPPORTUNITY,
    {
      variables: { id: opportunityId },
      skip: !opportunityId,
      fetchPolicy: 'cache-and-network',
    }
  );

  // GraphQL mutations
  const [updateOpportunity, { loading: updating }] = useMutation(UPDATE_OPPORTUNITY, {
    onCompleted: () => {
      setShowEditModal(false);
      refetch();
    },
  });

  const [markWon, { loading: markingWon }] = useMutation(MARK_OPPORTUNITY_WON, {
    onCompleted: () => {
      setShowWonModal(false);
      navigate('/sales/opportunities');
    },
  });

  const [markLost, { loading: markingLost }] = useMutation(MARK_OPPORTUNITY_LOST, {
    onCompleted: () => {
      setShowLostModal(false);
      navigate('/sales/opportunities');
    },
  });

  const [deleteOpportunity, { loading: deleting }] = useMutation(DELETE_OPPORTUNITY, {
    onCompleted: () => {
      navigate('/sales/opportunities');
    },
  });

  // Product mutations
  const [addProduct, { loading: addingProduct }] = useMutation(ADD_OPPORTUNITY_PRODUCT, {
    onCompleted: () => {
      setShowAddProductModal(false);
      setSelectedProductId('');
      setNewProductPrice('');
      setNewProductQuantity('1');
      setNewProductCustomSku('');
      refetch();
    },
  });

  // Handle product selection - auto-populate price
  const handleProductSelect = (productId: string) => {
    setSelectedProductId(productId);
    const selectedProduct = productsList.find((p: Product) => p.id === productId);
    if (selectedProduct) {
      setNewProductPrice(formatCurrencyInput(selectedProduct.unitPrice.toString()));
    }
  };

  const [removeProduct] = useMutation(REMOVE_OPPORTUNITY_PRODUCT, {
    onCompleted: () => {
      refetch();
    },
  });

  const opportunity = data?.opportunity;

  const handleOpenEditModal = () => {
    if (opportunity) {
      setEditValue(opportunity.value.toString());
      setEditStage(opportunity.stage);
      setEditProbability(opportunity.probability.toString());
      setEditExpectedClose(opportunity.expectedCloseDate.split('T')[0]);
      setEditNotes(opportunity.notes || '');
      setEditIsRecurring(opportunity.isRecurring);
    }
    setShowEditModal(true);
  };

  const handleSaveEdit = () => {
    if (!opportunityId) return;
    updateOpportunity({
      variables: {
        id: opportunityId,
        input: {
          value: parseCurrencyInput(editValue),
          stage: editStage,
          probability: parseInt(editProbability),
          expectedCloseDate: editExpectedClose,
          notes: editNotes || undefined,
          isRecurring: editIsRecurring,
        },
      },
    });
  };

  const handleMarkWon = () => {
    if (!opportunityId) return;
    markWon({
      variables: {
        id: opportunityId,
        input: {
          wonNotes: wonNotes || undefined,
        },
      },
    });
  };

  const handleMarkLost = () => {
    if (!opportunityId) return;
    markLost({
      variables: {
        id: opportunityId,
        input: {
          lostReason: lostReason || undefined,
          lostToCompetitorName: lostToCompetitor || undefined,
        },
      },
    });
  };

  const handleDelete = () => {
    if (!opportunityId) return;
    if (confirm('Are you sure you want to delete this opportunity?')) {
      deleteOpportunity({
        variables: { id: opportunityId },
      });
    }
  };

  const handleStageChange = (newStage: string) => {
    setEditStage(newStage);
    const stageInfo = OPPORTUNITY_STAGES.find(s => s.value === newStage);
    if (stageInfo) {
      setEditProbability(stageInfo.probability.toString());
    }
  };

  // Format currency with commas
  const formatCurrencyInput = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const parseCurrencyInput = (value: string): number => {
    return parseFloat(value.replace(/,/g, '')) || 0;
  };

  const handleValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCurrencyInput(e.target.value);
    setEditValue(formatted);
  };

  // Loading state
  if (loading && !opportunity) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-pond" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="card p-8 text-center">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 dark:text-red-400 mb-4">
          Error loading opportunity: {error.message}
        </p>
        <div className="flex justify-center gap-3">
          <Link to="/sales/opportunities" className="btn-secondary">
            <ArrowLeft className="w-4 h-4" />
            Back to Opportunities
          </Link>
          <button onClick={() => refetch()} className="btn-primary">
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Not found state
  if (!opportunity) {
    return (
      <div className="card p-8 text-center">
        <Target className="w-12 h-12 text-french-gray mx-auto mb-4" />
        <p className="text-slate dark:text-mist mb-4">Opportunity not found</p>
        <Link to="/sales/opportunities" className="btn-secondary">
          <ArrowLeft className="w-4 h-4" />
          Back to Opportunities
        </Link>
      </div>
    );
  }

  const stageBadge = STAGE_BADGES[opportunity.stage];
  const isActive = !['WON', 'LOST'].includes(opportunity.stage);
  const contactName = opportunity.primaryContact
    ? `${opportunity.primaryContact.firstName} ${opportunity.primaryContact.lastName}`
    : 'No contact';

  return (
    <div className="space-y-6">
      {/* Back Link */}
      <Link
        to="/sales/opportunities"
        className="inline-flex items-center gap-2 text-sm text-chalkboard dark:text-rain-cloud hover:text-pond dark:hover:text-pond-light transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Opportunities
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-violet/10 dark:bg-violet-light/20 flex items-center justify-center">
            <Target className="w-7 h-7 text-violet dark:text-violet-light" />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-slate dark:text-mist">
                {opportunity.organization.name}
              </h1>
              <span className={cn('badge', stageBadge.className)}>
                {stageBadge.label}
              </span>
            </div>
            <p className="text-chalkboard dark:text-rain-cloud mt-1">
              {opportunity.name} • Created {formatDate(new Date(opportunity.createdAt))}
            </p>
            {opportunity.isRecurring && (
              <span className="text-sm text-turquoise dark:text-aqua">
                Annual Recurring
              </span>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleDelete}
            className="btn-secondary text-red-600 hover:text-red-700 dark:text-red-400"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
          {isActive && (
            <>
              <button onClick={handleOpenEditModal} className="btn-secondary">
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={() => setShowWonModal(true)}
                className="btn-secondary text-turquoise dark:text-aqua"
              >
                <CheckCircle className="w-4 h-4" />
                Mark Won
              </button>
              <button
                onClick={() => setShowLostModal(true)}
                className="btn-secondary text-rust dark:text-salmon"
              >
                <XCircle className="w-4 h-4" />
                Mark Lost
              </button>
            </>
          )}
        </div>
      </div>

      {/* Opportunity Details Card */}
      <div className="card">
        <div className="card-header">
          <h2 className="font-semibold text-slate dark:text-mist">Opportunity Details</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pb-4 border-b border-gray dark:border-night-pond">
            <div>
              <p className="text-sm text-chalkboard dark:text-rain-cloud">Stage</p>
              <span className={cn('badge mt-1', stageBadge.className)}>
                {stageBadge.label}
              </span>
            </div>
            <div>
              <p className="text-sm text-chalkboard dark:text-rain-cloud">Deal Value</p>
              <div className="flex items-center gap-1 mt-1">
                <DollarSign className="w-4 h-4 text-turquoise dark:text-aqua" />
                <span className="font-semibold text-slate dark:text-mist">
                  {formatCurrency(opportunity.value)}
                </span>
              </div>
            </div>
            <div>
              <p className="text-sm text-chalkboard dark:text-rain-cloud">Probability</p>
              <div className="flex items-center gap-2 mt-1">
                <div className="w-16 h-2 bg-gray-light dark:bg-night-pond rounded-full overflow-hidden">
                  <div
                    className={cn(
                      'h-full rounded-full',
                      opportunity.probability >= 75
                        ? 'bg-turquoise'
                        : opportunity.probability >= 50
                        ? 'bg-yellow'
                        : 'bg-orange'
                    )}
                    style={{ width: `${opportunity.probability}%` }}
                  />
                </div>
                <span className="font-medium text-slate dark:text-mist">
                  {opportunity.probability}%
                </span>
              </div>
            </div>
            <div>
              <p className="text-sm text-chalkboard dark:text-rain-cloud">Expected Close</p>
              <div className="flex items-center gap-2 mt-1">
                <Calendar className="w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                <span className="text-slate dark:text-mist">
                  {formatDate(new Date(opportunity.expectedCloseDate))}
                </span>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-pale-gray-2 dark:bg-night-pond flex items-center justify-center">
                <Building2 className="w-5 h-5 text-chalkboard dark:text-rain-cloud" />
              </div>
              <div>
                <p className="text-sm text-chalkboard dark:text-rain-cloud">Organization</p>
                <Link
                  to={`/sales/prospects/${opportunity.organizationId}`}
                  className="font-medium text-pond dark:text-pond-light hover:underline"
                >
                  {opportunity.organization.name}
                </Link>
                <p className="text-xs text-chalkboard dark:text-rain-cloud">
                  {opportunity.organization.type}
                </p>
              </div>
            </div>
            {opportunity.primaryContact && (
              <>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-pale-gray-2 dark:bg-night-pond flex items-center justify-center">
                    <User className="w-5 h-5 text-chalkboard dark:text-rain-cloud" />
                  </div>
                  <div>
                    <p className="text-sm text-chalkboard dark:text-rain-cloud">Primary Contact</p>
                    <p className="font-medium text-slate dark:text-mist">{contactName}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-pale-gray-2 dark:bg-night-pond flex items-center justify-center">
                    <Mail className="w-5 h-5 text-chalkboard dark:text-rain-cloud" />
                  </div>
                  <div>
                    <p className="text-sm text-chalkboard dark:text-rain-cloud">Email</p>
                    <a
                      href={`mailto:${opportunity.primaryContact.email}`}
                      className="font-medium text-pond dark:text-pond-light hover:underline"
                    >
                      {opportunity.primaryContact.email}
                    </a>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Products Card */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="font-semibold text-slate dark:text-mist flex items-center gap-2">
            <Package className="w-5 h-5" />
            Products
          </h2>
          {isActive && (
            <button
              onClick={() => setShowAddProductModal(true)}
              className="btn-secondary btn-sm"
            >
              <Plus className="w-4 h-4" />
              Add Product
            </button>
          )}
        </div>
        <div className="card-body">
          {opportunity.products && opportunity.products.length > 0 ? (
            <div className="space-y-3">
              {opportunity.products.map((product: OpportunityProduct) => (
                <div
                  key={product.id}
                  className="flex items-center justify-between py-3 border-b border-gray dark:border-night-pond last:border-0"
                >
                  <div>
                    <p className="font-medium text-slate dark:text-mist">
                      {product.product?.name || 'Product'}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-chalkboard dark:text-rain-cloud">
                      <span>SKU: {product.customSku || product.product?.sku || '-'}</span>
                    </div>
                    {product.notes && (
                      <p className="text-sm text-chalkboard dark:text-rain-cloud mt-1">
                        {product.notes}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-chalkboard dark:text-rain-cloud">
                      Qty: {product.quantity}
                    </span>
                    <span className="font-semibold text-slate dark:text-mist">
                      {formatCurrency(product.price * product.quantity)}
                    </span>
                    {isActive && (
                      <button
                        onClick={() => {
                          if (confirm('Remove this product?')) {
                            removeProduct({ variables: { id: product.id } });
                          }
                        }}
                        className="btn-ghost btn-icon btn-sm text-rust dark:text-salmon"
                        title="Remove product"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-3 font-semibold">
                <span className="text-slate dark:text-mist">Total</span>
                <span className="text-turquoise dark:text-aqua">
                  {formatCurrency(
                    opportunity.products.reduce(
                      (sum: number, p: OpportunityProduct) => sum + p.price * p.quantity,
                      0
                    )
                  )}
                </span>
              </div>
            </div>
          ) : (
            <p className="text-chalkboard dark:text-rain-cloud italic text-center py-4">
              No products added yet. Click &quot;Add Product&quot; to add line items.
            </p>
          )}
        </div>
      </div>

      {/* Notes Card */}
      <div className="card">
        <div className="card-header">
          <h2 className="font-semibold text-slate dark:text-mist flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Notes
          </h2>
        </div>
        <div className="card-body">
          {opportunity.notes ? (
            <p className="text-slate dark:text-mist whitespace-pre-wrap">
              {opportunity.notes}
            </p>
          ) : (
            <p className="text-chalkboard dark:text-rain-cloud italic">
              No notes added yet.
            </p>
          )}
        </div>
      </div>

      {/* Lost Info - show if opportunity is lost */}
      {opportunity.stage === 'LOST' && (
        <div className="card border-rust/30 dark:border-salmon/30">
          <div className="card-header bg-rust/10 dark:bg-salmon/10">
            <h2 className="font-semibold text-rust dark:text-salmon flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Lost Details
            </h2>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-2 gap-4">
              {opportunity.lostToCompetitorName && (
                <div>
                  <p className="text-sm text-chalkboard dark:text-rain-cloud">Lost To</p>
                  <p className="font-medium text-slate dark:text-mist">
                    {opportunity.lostToCompetitorName}
                  </p>
                </div>
              )}
              {opportunity.lostReason && (
                <div>
                  <p className="text-sm text-chalkboard dark:text-rain-cloud">Reason</p>
                  <p className="font-medium text-slate dark:text-mist">
                    {opportunity.lostReason}
                  </p>
                </div>
              )}
              {opportunity.actualCloseDate && (
                <div>
                  <p className="text-sm text-chalkboard dark:text-rain-cloud">Closed Date</p>
                  <p className="font-medium text-slate dark:text-mist">
                    {formatDate(new Date(opportunity.actualCloseDate))}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Won Info - show if opportunity is won */}
      {opportunity.stage === 'WON' && (
        <div className="card border-turquoise/30 dark:border-aqua/30">
          <div className="card-header bg-turquoise/10 dark:bg-aqua/10">
            <h2 className="font-semibold text-turquoise dark:text-aqua flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Won Details
            </h2>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-2 gap-4">
              {opportunity.actualCloseDate && (
                <div>
                  <p className="text-sm text-chalkboard dark:text-rain-cloud">Closed Date</p>
                  <p className="font-medium text-slate dark:text-mist">
                    {formatDate(new Date(opportunity.actualCloseDate))}
                  </p>
                </div>
              )}
              {opportunity.wonNotes && (
                <div>
                  <p className="text-sm text-chalkboard dark:text-rain-cloud">Notes</p>
                  <p className="font-medium text-slate dark:text-mist">
                    {opportunity.wonNotes}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Opportunity Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Opportunity"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Stage
              </label>
              <select
                value={editStage}
                onChange={(e) => handleStageChange(e.target.value)}
                className="select"
              >
                {OPPORTUNITY_STAGES.map((stage) => (
                  <option key={stage.value} value={stage.value}>
                    {stage.label} ({stage.probability}%)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Deal Value
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                <input
                  type="text"
                  value={editValue}
                  onChange={handleValueChange}
                  className="input"
                  style={{ paddingLeft: '2.5rem' }}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Probability (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={editProbability}
                onChange={(e) => setEditProbability(e.target.value)}
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Expected Close
              </label>
              <input
                type="date"
                value={editExpectedClose}
                onChange={(e) => setEditExpectedClose(e.target.value)}
                className="input"
              />
            </div>
          </div>

          <label className="flex items-center gap-3 p-3 rounded-lg border border-gray dark:border-night-pond cursor-pointer">
            <input
              type="checkbox"
              checked={editIsRecurring}
              onChange={(e) => setEditIsRecurring(e.target.checked)}
              className="w-4 h-4"
            />
            <span className="text-sm font-medium text-slate dark:text-mist">
              Annual Recurring Relationship
            </span>
          </label>

          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Notes
            </label>
            <textarea
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              placeholder="Additional notes..."
              rows={3}
              className="input resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setShowEditModal(false)} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={updating}
              className="btn-primary"
            >
              {updating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Mark Won Modal */}
      <Modal
        isOpen={showWonModal}
        onClose={() => setShowWonModal(false)}
        title="Mark Opportunity as Won"
      >
        <div className="space-y-4">
          <div className="p-3 bg-turquoise/10 dark:bg-aqua/10 rounded-lg">
            <p className="text-sm text-turquoise dark:text-aqua">
              Congratulations! This will mark the opportunity as won.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Notes (Optional)
            </label>
            <textarea
              value={wonNotes}
              onChange={(e) => setWonNotes(e.target.value)}
              placeholder="Any notes about the win..."
              rows={3}
              className="input resize-none"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setShowWonModal(false)} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={handleMarkWon}
              disabled={markingWon}
              className="btn-primary bg-turquoise hover:bg-turquoise/90"
            >
              {markingWon ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Marking...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Mark as Won
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Mark Lost Modal */}
      <Modal
        isOpen={showLostModal}
        onClose={() => setShowLostModal(false)}
        title="Mark Opportunity as Lost"
      >
        <div className="space-y-4">
          <div className="p-3 bg-rust/10 dark:bg-salmon/10 rounded-lg">
            <p className="text-sm text-rust dark:text-salmon">
              This will mark the opportunity as lost.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Lost To (Competitor)
            </label>
            <input
              type="text"
              value={lostToCompetitor}
              onChange={(e) => setLostToCompetitor(e.target.value)}
              placeholder="Enter competitor name..."
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Reason for Loss
            </label>
            <select
              value={lostReason}
              onChange={(e) => setLostReason(e.target.value)}
              className="select"
            >
              <option value="">Select reason...</option>
              {LOSS_REASONS.map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setShowLostModal(false)} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={handleMarkLost}
              disabled={markingLost}
              className="btn-primary bg-rust hover:bg-rust/90"
            >
              {markingLost ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Marking...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4" />
                  Mark as Lost
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Add Product Modal */}
      <Modal
        isOpen={showAddProductModal}
        onClose={() => setShowAddProductModal(false)}
        title="Add Product"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Select Product <span className="text-sunset">*</span>
            </label>
            <select
              value={selectedProductId}
              onChange={(e) => handleProductSelect(e.target.value)}
              className="select"
            >
              <option value="">-- Select a product --</option>
              {productsList.map((product: Product) => (
                <option key={product.id} value={product.id}>
                  {product.name} ({product.sku}) - {formatCurrency(product.unitPrice)}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Price <span className="text-sunset">*</span>
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                <input
                  type="text"
                  value={newProductPrice}
                  onChange={(e) => setNewProductPrice(formatCurrencyInput(e.target.value))}
                  placeholder="0"
                  className="input"
                  style={{ paddingLeft: '2.5rem' }}
                />
              </div>
              <p className="text-xs text-chalkboard dark:text-rain-cloud mt-1">
                Auto-filled from product, can be adjusted
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Quantity
              </label>
              <input
                type="number"
                min="1"
                value={newProductQuantity}
                onChange={(e) => setNewProductQuantity(e.target.value)}
                className="input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Custom SKU (Optional)
            </label>
            <input
              type="text"
              value={newProductCustomSku}
              onChange={(e) => setNewProductCustomSku(e.target.value)}
              placeholder="Prospect-specific SKU or part number"
              className="input"
            />
            <p className="text-xs text-chalkboard dark:text-rain-cloud mt-1">
              Use this for the prospect&apos;s internal part number
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setShowAddProductModal(false)} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={() => {
                if (!opportunityId || !selectedProductId || !newProductPrice) return;
                addProduct({
                  variables: {
                    input: {
                      opportunityId,
                      productId: selectedProductId,
                      customSku: newProductCustomSku || undefined,
                      price: parseCurrencyInput(newProductPrice),
                      quantity: parseInt(newProductQuantity) || 1,
                    },
                  },
                });
              }}
              disabled={addingProduct || !selectedProductId || !newProductPrice}
              className="btn-primary"
            >
              {addingProduct ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Add Product
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
